import React, { useRef, useEffect } from "react";
import { Typography, Button, Tooltip, message } from "antd";
import { useNavigate } from "react-router-dom";
import { EditOutlined, DeleteOutlined, PlusOutlined, EyeOutlined, FilePdfOutlined } from "@ant-design/icons";
import api from "src/config/api";
import {
  FormDatePicker,
  FormText,
  FormSelect,
} from "src/components/Form";
import eventBus from "src/common/eventBus";
import Table from "src/components/Table/Table";
import _ from "lodash";
import { formatDate } from "src/shared/dateTimeUtils";

const Contracts = () => {
  const navigate = useNavigate();
  const tableRef = useRef();
  const actions = [
    <Button
      icon={<PlusOutlined />}
      onClick={() => navigate("new")}
      type="primary"
    >
      Add
    </Button>,
  ];

  const filterInputs = [
    <FormText name="customerName" placeholder="Customer Name" allowClear />,
    <FormSelect 
      name="bikeId" 
      placeholder="Bike" 
      allowClear
      loadOptions={() => api.get('bikes', { params: { limit: 100 } })}
      onLoadOptionsSuccess={(data) => {
        return data.data.map(bike => ({
          label: `${bike.bikeName} (${bike.licenseNumber || 'No License'})`,
          value: bike.id
        }));
      }}
    />,
    <FormSelect
      name="status"
      placeholder="Status"
      allowClear
      defaultValue="active"
      options={[
        { label: "Active", value: "active" },
        { label: "Completed", value: "completed" },
        { label: "All", value: "" }
      ]}
    />,
    <FormDatePicker name="startDate" placeholder="Start Date" />,
    <FormDatePicker name="endDate" placeholder="End Date" />,
  ];

  // Add useEffect to fetch bikes for the dropdown
  useEffect(() => {
    const fetchBikes = async () => {
      try {
        const response = await api.get('bikes', { params: { limit: 100 } });
        if (response.data && response.data.data) {
          const bikeOptions = response.data.data.map(bike => ({
            label: `${bike.bikeName} (${bike.licenseNumber || 'No License'})`,
            value: bike.id
          }));
          
          // Update the bikeId select options
          const bikeIdFilter = filterInputs.find(input => input.props.name === 'bikeId');
          if (bikeIdFilter) {
            bikeIdFilter.props.options = bikeOptions;
          }
        }
      } catch (error) {
        console.error('Error fetching bikes for dropdown:', error);
      }
    };
    
    fetchBikes();
  }, []);

  const leftMoreFilters = [
    // <FormText name="customerName" label="Customer Name" />,
    // <FormText name="motorbikeName" label="Motorbike Name" />,
    // <FormText name="licensePlate" label="License Plate" />,
    // <FormDatePicker name="startDate" label="Start Date" />,
    // <FormDatePicker name="endDate" label="End Date" />,
    // <FormDateRangePicker
    //   name="createDateRange"
    //   label="Created Between"
    //   startText="From"
    //   endText="To"
    // />,
  ];

  const rightMoreFilters = [
    // <FormSelect
    //   name="priceRange"
    //   label="Price Range"
    //   options={[
    //     { label: "Under $100", value: "0-100" },
    //     { label: "$100 - $500", value: "100-500" },
    //     { label: "$500 - $1000", value: "500-1000" },
    //     { label: "Over $1000", value: "1000+" },
    //   ]}
    //   allowClear
    // />,
  ];

  const columns = [
    {
      dataIndex: "createDate",
      title: "Created On",
      width: "120px",
      render: (date) => formatDate(date),
      defaultSortOrder: "descend",
    },
    {
      dataIndex: "customerName",
      title: "Customer Name",
      width: "200px",
    },
    {
      dataIndex: "status",
      title: "Status",
      width: "100px",
      render: (status) => {
        const statusText = status === 'completed' ? 'Completed' : 'Active';
        const color = status === 'completed' ? '#52c41a' : '#1890ff';
        return (
          <span style={{ 
            color: 'white', 
            backgroundColor: color, 
            padding: '3px 10px', 
            borderRadius: '12px',
            fontSize: '12px',
            fontWeight: '500',
            display: 'inline-block',
            textAlign: 'center'
          }}>
            {statusText}
          </span>
        );
      },
    },
    {
      title: "Bike",
      width: "200px",
      render: (_, row) => (
        <span>
          {row.bikeName}
          {row.licenseNumber && (
            <span style={{ color: "#888" }}> ({row.licenseNumber})</span>
          )}
        </span>
      ),
    },
    {
      title: "Rental Period",
      width: "220px",
      render: (_, row) => {

        const startFormatted = formatDate(row.startDate);
        const endFormatted = formatDate(row.endDate);

        if (startFormatted && endFormatted) {
          return (
            <div style={{ fontSize: "0.9em" }}>
              <div>From: {startFormatted}</div>
              <div>To: {endFormatted}</div>
            </div>
          );
        } else if (startFormatted) {
          return `From ${startFormatted}`;
        } else if (endFormatted) {
          return `Until ${endFormatted}`;
        }
        return "";
      },
    },
    {
      dataIndex: "price",
      title: "Price",
      width: "120px",
      render: (price) => price && `${price.toLocaleString('vi-VN')} VND`,
    },
    {
      dataIndex: "actions",
      title: "Actions",
      width: "240px",
      render: (_, row) => (
        <div style={{ display: 'flex', gap: '8px' }}>
          <Tooltip title="View">
            <Button
              icon={<EyeOutlined />}
              onClick={() => navigate(`/contracts/${row.id}`)}
            />
          </Tooltip>
          <Tooltip title="Edit">
            <Button
              icon={<EditOutlined />}
              onClick={() => navigate(`/contracts/${row.id}/edit`)}
            />
          </Tooltip>
          <Tooltip title="Download as PDF">
            <Button
              icon={<FilePdfOutlined />}
              onClick={() => generatePdf(row)}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Button
              icon={<DeleteOutlined />}
              danger
              onClick={() => {
                eventBus.emit("dialog", {
                  type: "confirm",
                  message: `Are you sure to delete contract for ${row.customerName}?`,
                  onOk: async () => {
                    await api.delete(`contracts/${row.id}`);
                  },
                  onFinish: () => {
                    tableRef.current.refresh();
                  },
                });
              }}
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  const generatePdf = async (contract) => {
    try {
      message.loading({ content: 'Generating PDF...', key: 'pdfGeneration', duration: 0 });
      
      // First, we need to navigate to the contract detail page and get the rendered HTML
      window.open(`/contracts/${contract.id}?download=true`, '_blank');
      
      message.success({ content: 'PDF generation started in new tab', key: 'pdfGeneration' });
    } catch (error) {
      console.error('Error generating PDF:', error);
      message.error({ content: 'Failed to generate PDF', key: 'pdfGeneration' });
    }
  };

  return (
    <>
      <Typography.Title level={4} className="mb-4">
        Contracts
      </Typography.Title>
      <Table
        ref={tableRef}
        url={"contracts"}
        actions={actions}
        filterInputs={filterInputs}
        leftMoreFilters={leftMoreFilters}
        rightMoreFilters={rightMoreFilters}
        columns={columns}
      ></Table>
    </>
  );
};

export default Contracts;
